import React, { useState, useEffect, useRef } from 'react';
import './ACEModelPanel.css';

const API = import.meta.env.VITE_ACE_API_URL || 'http://localhost:8001';

const ACEModelPanel = ({ isVisible, onClose }) => {
  // KG stats
  const [stats, setStats] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [systems, setSystems] = useState([]);

  // Query state
  const [question, setQuestion] = useState('');
  const [platform, setPlatform] = useState('');
  const [system, setSystem] = useState('');
  const [mode, setMode] = useState('full'); // 'chroma' | 'kg' | 'full'
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // KG browser state
  const [browserPlatform, setBrowserPlatform] = useState('ACE EV');
  const [browserSystem, setBrowserSystem] = useState('');
  const [browserSystems, setBrowserSystems] = useState([]);
  const [browserResults, setBrowserResults] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);

  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Elapsed timer while loading
  useEffect(() => {
    if (loading) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [loading]);

  // Load stats + platforms on mount
  useEffect(() => {
    if (!isVisible) return;
    fetch(`${API}/health`).catch(() => {});
    fetch(`${API}/kg/stats`)
      .then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/kg/platforms`)
      .then(r => r.json()).then(p => {
        const list = Array.isArray(p) ? p : [];
        setPlatforms(list);
        setBrowserPlatform(list.includes('ACE EV') ? 'ACE EV' : list[0] || '');
      }).catch(() => setPlatforms([]));
    inputRef.current?.focus();
  }, [isVisible]);

  // Load systems when platform filter changes
  useEffect(() => {
    if (!platform) { setSystems([]); return; }
    fetch(`${API}/kg/systems?platform=${encodeURIComponent(platform)}`)
      .then(r => r.json()).then(s => setSystems(Array.isArray(s) ? s : [])).catch(() => setSystems([]));
  }, [platform]);

  // Load browser systems when browser platform changes
  useEffect(() => {
    // Closed panel: no request. browserPlatform starts as 'ACE EV', so without this the
    // ACE API was called on every page load even though nobody opened the panel.
    if (!isVisible || !browserPlatform) return;
    fetch(`${API}/kg/systems?platform=${encodeURIComponent(browserPlatform)}`)
      .then(r => r.json()).then(s => { setBrowserSystems(Array.isArray(s) ? s : []); setBrowserSystem(''); setBrowserResults([]); })
      .catch(() => setBrowserSystems([]));
  }, [browserPlatform, isVisible]);

  const handleQuery = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setResult(null);
    setError('');

    const endpoint = mode === 'kg' ? '/kg/query' : mode === 'chroma' ? '/query' : '/query/full';
    const body = mode === 'kg'
      ? { platform: platform || undefined, system: system || undefined, limit: 8 }
      : { question: question.trim(), platform: platform || undefined, system: system || undefined, top_k: 3, max_tokens: 120 };

    // Abort after 5 minutes — matches OLLAMA_TIMEOUT=300s on the server
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 5 * 60 * 1000);

    try {
      const r = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!r.ok) throw new Error(`API error ${r.status}`);
      setResult(await r.json());
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out after 5 minutes. Try a shorter question or reduce max_tokens.');
      } else {
        setError(`Query failed: ${err.message}`);
      }
    } finally {
      clearTimeout(abortTimer);
      setLoading(false);
    }
  };

  const handleBrowse = async () => {
    if (!browserPlatform) return;
    setBrowserLoading(true);
    setBrowserResults([]);
    try {
      const r = await fetch(`${API}/kg/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform: browserPlatform, system: browserSystem || undefined, limit: 12 }),
      });
      const data = await r.json();
      setBrowserResults(data.results || []);
    } catch (err) {
      setBrowserResults([]);
    } finally {
      setBrowserLoading(false);
    }
  };

  const quickQueries = [
    'Vehicle shows 0% battery suddenly — what is happening and how to fix?',
    'Charging stopped at 78% SOC and service lamp is ON — what should I check?',
    'Customer says vehicle won\'t start charging again after it stopped mid-charge',
    'BMS ECU showing DTCs with Description Not Available in Beta tool',
    'What firmware update fixes SOC drop issues on this platform?',
    'Motor getting too hot — what are the common causes and corrective actions?',
  ];

  if (!isVisible) return null;

  return (
    <div className="ace-panel-overlay fade-in">
      <div className="ace-panel">

        {/* Header */}
        <div className="ace-header">
          <div className="ace-header-left">
            <div className="ace-logo">
              <span className="ace-logo-icon">⬡</span>
              <div>
                <div className="ace-title">Failure matrix</div>
                <div className="ace-subtitle">Failure-mode knowledge · offline RAG</div>
              </div>
            </div>
            {stats && (
              <div className="ace-stats-row">
                <div className="ace-stat">
                  <span className="ace-stat-val">{stats.Platform}</span>
                  <span className="ace-stat-lbl">Platforms</span>
                </div>
                <div className="ace-stat-div" />
                <div className="ace-stat">
                  <span className="ace-stat-val">{stats.System}</span>
                  <span className="ace-stat-lbl">Systems</span>
                </div>
                <div className="ace-stat-div" />
                <div className="ace-stat">
                  <span className="ace-stat-val">{stats.FailureCase}</span>
                  <span className="ace-stat-lbl">Failure Cases</span>
                </div>
                <div className="ace-stat-div" />
                <div className="ace-stat">
                  <span className="ace-stat-val">{stats.relationships?.AFFECTS || 0}</span>
                  <span className="ace-stat-lbl">KG Links</span>
                </div>
              </div>
            )}
          </div>
          <button className="ace-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="ace-body">

          {/* LEFT: Query Panel */}
          <div className="ace-query-col">

            {/* Mode tabs */}
            <div className="ace-mode-tabs">
              {[['full', '⬡ Hybrid (KG + RAG)'], ['chroma', '🔍 Vector Search'], ['kg', '🕸 KG Browse']].map(([m, label]) => (
                <button
                  key={m}
                  className={`ace-mode-tab ${mode === m ? 'active' : ''}`}
                  onClick={() => { setMode(m); setResult(null); setError(''); }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Filters */}
            <div className="ace-filters">
              <select
                className="ace-select"
                value={platform}
                onChange={e => { setPlatform(e.target.value); setSystem(''); }}
              >
                <option value="">All Platforms</option>
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                className="ace-select"
                value={system}
                onChange={e => setSystem(e.target.value)}
                disabled={!platform || systems.length === 0}
              >
                <option value="">All Systems</option>
                {systems.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Quick queries */}
            <div className="ace-quick-chips">
              {quickQueries.map((q, i) => (
                <button key={i} className="ace-chip" onClick={() => { setQuestion(q); setMode('full'); }}>
                  {q}
                </button>
              ))}
            </div>

            {/* Query input */}
            {mode !== 'kg' && (
              <form className="ace-input-form" onSubmit={handleQuery}>
                <textarea
                  ref={inputRef}
                  className="ace-textarea"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  placeholder="Describe the problem you're seeing — e.g. 'Customer's vehicle stopped charging at 78% and service lamp is ON. What should I check?'"
                  rows={3}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuery(e); }}}
                />
                <button className="ace-submit-btn" type="submit" disabled={loading || !question.trim()}>
                  {loading ? <span className="ace-spinner" /> : '⬡ QUERY'}
                </button>
                {loading && (
                  <div className="ace-loading-bar">
                    <div className="ace-loading-pulse" />
                    <span>Generating answer with offline LLM... {elapsed}s</span>
                    <span className="ace-loading-hint">(This may take 1–3 minutes on CPU)</span>
                  </div>
                )}
              </form>
            )}

            {mode === 'kg' && (
              <>
                <button className="ace-submit-btn kg-browse-btn" onClick={handleQuery} disabled={loading}>
                  {loading ? <span className="ace-spinner" /> : '🕸 FETCH FROM KG'}
                </button>
                {loading && (
                  <div className="ace-loading-bar">
                    <div className="ace-loading-pulse" />
                    <span>Querying Knowledge Graph... {elapsed}s</span>
                  </div>
                )}
              </>
            )}

            {/* Error */}
            {error && <div className="ace-error">{error}</div>}

            {/* Results */}
            {result && mode !== 'kg' && (
              <div className="ace-result-block">
                <div className="ace-result-header">
                  <span className="ace-result-icon">⬡</span>
                  <span>AI RESPONSE</span>
                  {result.kg_context && <span className="ace-kg-badge">+ KG</span>}
                </div>
                <div className="ace-answer">{result.answer}</div>

                {result.sources?.length > 0 && (
                  <>
                    <div className="ace-sources-header">SOURCES ({result.sources.length})</div>
                    <div className="ace-sources">
                      {result.sources.map((s, i) => (
                        <div key={i} className="ace-source-card">
                          <div className="ace-source-meta">
                            <span className="ace-platform-tag">{s.platform}</span>
                            <span className="ace-system-tag">{s.system}</span>
                            <span className="ace-row-tag">row {s.row}</span>
                          </div>
                          <div className="ace-source-snippet">{s.snippet}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* KG mode results */}
            {result && mode === 'kg' && (
              <div className="ace-result-block">
                <div className="ace-result-header">
                  <span>KG RESULTS — {result.count} records</span>
                </div>
                <div className="ace-kg-records">
                  {result.results?.map((r, i) => (
                    <div key={i} className="ace-kg-record">
                      <div className="ace-source-meta">
                        <span className="ace-platform-tag">{r.platform}</span>
                        <span className="ace-system-tag">{r.system}</span>
                        <span className="ace-row-tag">row {r.row}</span>
                      </div>
                      <div className="ace-kg-field"><span>Failure:</span> {r.failure_mode}</div>
                      <div className="ace-kg-field"><span>Root Cause:</span> {r.root_cause}</div>
                      <div className="ace-kg-field ace-fix"><span>Fix:</span> {r.corrective_action}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: KG Browser */}
          <div className="ace-kg-col">
            <div className="ace-kg-browser-header">KG browser</div>

            <div className="ace-kg-filters">
              <select
                className="ace-select"
                value={browserPlatform}
                onChange={e => setBrowserPlatform(e.target.value)}
              >
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                className="ace-select"
                value={browserSystem}
                onChange={e => setBrowserSystem(e.target.value)}
                disabled={browserSystems.length === 0}
              >
                <option value="">All Systems ({browserSystems.length})</option>
                {browserSystems.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button className="ace-browse-btn" onClick={handleBrowse} disabled={browserLoading}>
                {browserLoading ? <span className="ace-spinner" /> : 'BROWSE'}
              </button>
            </div>

            <div className="ace-kg-tree">
              {browserSystems.length > 0 && !browserLoading && browserResults.length === 0 && (
                <div className="ace-kg-hint">
                  Select a system and click BROWSE to explore failure cases
                </div>
              )}
              {browserLoading && <div className="ace-loading-text">Loading KG data...</div>}
              {browserResults.map((r, i) => (
                <div key={i} className="ace-tree-node">
                  <div className="ace-tree-system">{r.system}</div>
                  <div className="ace-tree-failure">{r.failure_mode?.slice(0, 80)}{r.failure_mode?.length > 80 ? '...' : ''}</div>
                  <div className="ace-tree-fix">{r.corrective_action?.slice(0, 80)}{r.corrective_action?.length > 80 ? '...' : ''}</div>
                </div>
              ))}
            </div>

            <div className="ace-api-footer">
              API: <span>{import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}</span> &nbsp;|&nbsp; Docs: <span>{(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000') + '/docs'}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ACEModelPanel;
